define((require) => {
    "use strict";

    const _ = require("underscore");
    const Backbone = require("backbone");
    const CompositeWidgetModel = require("shared/ui/experience/widgets/compositeWidget/CompositeWidgetModel");
    const EditorActivities = require("editor/ui/experience/editor/activity/EditorActivities");
    const EditorActivityManager = require("editor/ui/experience/editor/activity/EditorActivityManager");
    const EditorConstants = require("editor/ui/experience/editor/EditorConstants");
    const GuidelineCollection = require("ui/editor/guideline/GuidelineCollection");
    const Scene = require("shared/ui/experience/Scene");
    /**
     * @class {Scene}
     * Scene Model Editor Mixin.
     *
     * @author Gorm Casper
     * @author Bo Gotthardt
     */
    return Scene.mixin("SceneEditorMixin", {

        embedded: true, // since it has an id and we do not want to call the server on sync!

        defaults: {
            lastCommentedAt: null,
            unread:          null,
            guidelines:      [],
            widgetTabOrderMetaData: [],
            widgetTabOrder: [],

            // Not sure if I want to maintain two different lists here, may just be easier to have a single list.
            removedWidgetTabOrderMetaData: [],
            removedWidgetTabOrder: []
        },

        computed: {
            // Transient property used for unread comment handling
            unread: {}
        },

        relations: (Scene.prototype.relations || []).concat([{
            type: Backbone.Many,
            key: "guidelines",
            collectionType: GuidelineCollection
        }]),

        transient: ["scale"],

        // Clear these fields on Model.clone({duplicate: true}) (also)...
        clearOnClone(options) {
            if (options.duplicate) {
                // will already ignore transient properties, i.e. experience
                return  ["lastCommentedAt", "unread", "widgetTabOrder", "widgetTabOrderMetaData", "removedWidgetTabOrder", "removedWidgetTabOrderMetaData"];
            }
        },

        // Update these fields for any scene always after a mutable server sync request...
        updateOnSync: ["lastCommentedAt"], // id always updated!

        /**
         * Adds a new widget (model) to the scene.
         *
         * @param {AbstractWidgetModel} widget The widget model to add.
         * @param {Object} options Optional options object.
         */
        addWidget(widget, options) {
            const widgets = this.get("widgets");

            if (options && options.setLayerIndex) {
                widget.set("layerIndex", widgets.length);
            }

            widgets.push(widget, options);

            return widget;
        },

        /**
         * Gets the highest index from the widgets array.
         *
         * @param {Array} widgets The widgets array.
         */
        getHighestIndex(widgets = this.get("widgets").models) {
            let highest = 0;

            _.each(widgets, (w) => {
                highest = w.get("layerIndex") > highest ? w.get("layerIndex") : highest;
            });

            return highest;
        },

        /**
         * Gets the lowest index from the widgets array.
         *
         * @param {Array} widgets The widgets array.
         */
        getLowestIndex(widgets = this.get("widgets").models) {
            let lowest = this.getHighestIndex(widgets);

            _.each(widgets, (w) => {
                lowest = w.get("layerIndex") < lowest ? w.get("layerIndex") : lowest;
            });

            return lowest;
        },


        addWidgets(widgets, options) {
            _.each(widgets, function(widget) {
                this.addWidget(widget, options);
            }, this);

            if (!options.selectWidget) {
                this.trigger("addWidgets", widgets);
            }
        },

        /**
         * Deletes a widget from the experience.
         *
         * @param {widget}  The widget to delete.
         * @param {options}
         */
        deleteWidget: function deleteWidget(widget, options) {
            const widgets = this.get("widgets");
            widgets.remove(widget, options);
            widget.destroy();
            this.reindexLayers(widgets.models, 0, options);
        },

        deleteWidgetByID(uuid, options) {
            const widget = this.getWidgetByID(uuid);

            if (widget) {
                const widgetsInScene = this.get("widgets");
                widgetsInScene.remove(widget, options);
                widget.destroy();
                this.reindexLayers(widgetsInScene.models, 0, options);
            }
        },

        getWidgetByID(uuid) {
            const widgets = this.get("widgets");

            let output;

            widgets.models.forEach((widget) => {
                if (widget.get("uuid") === uuid) {
                    output = widget;
                    return widget;
                }
            });

            return output;
        },

        deleteWidgets: function(widgets, options) {
            _.each(widgets, function(widget) {
                this.deleteWidget(widget, options);
            }, this);
        },

        /**
         * reset all the layerIndex to its index of the collection
         */
        reindexLayers(widgets, offset = 0, options = {render: true}) {
            widgets.forEach((widget, idx) => {
                widget.set("layerIndex", idx + offset, options);
            });
        },

        /**
         * TODO: Remove this method. It already exists in Scene.js
        */
        widgetsByClass(widgetClass) {
            const widgets = this.get("widgets");

            if (widgets) {
                return widgets.filter((widget) =>
                    widget instanceof widgetClass);
            }

            return [];
        },

        /**
         * Send a widget to a specific layer
         *
         * Note: layering is completely relying on the order of widgets array,
         * each widget will render itself on the correct layer based on the layer index we give them
         * see {@link AbstractWidgetView#onRender}.
         *
         * access to the raw collection models, so we can bypass the Backbone events which we don't want
         *
         * @param widgetModel The widget to move
         * @param to The specific layer
         */
        orderWidget({
            widgetModel, to, siblingWidgets = null, newIndex
        }) {
            let models  = this.get("widgets").models;
            if (siblingWidgets) {
                models = siblingWidgets.models;
            }
            let index   = models.indexOf(widgetModel),
                targetIndex  = -1,
                lowestIndex = this.getLowestIndex(models),
                highestIndex = this.getHighestIndex(models);

            switch (to) {
                case EditorConstants.BRING_TO_FRONT.value:
                    targetIndex = highestIndex;
                    break;

                case EditorConstants.BRING_FORWARD.value:
                    targetIndex = Math.min(index + 1, highestIndex);
                    break;

                case EditorConstants.SEND_BACKWARD.value:
                    targetIndex = Math.max(index - 1, 0);
                    break;

                case EditorConstants.SEND_BACK.value:
                    targetIndex = 0;
                    break;

                case EditorConstants.BRING_TO_TARGET.value:
                    targetIndex = newIndex;
                    break;

                default:
                    // Will default to -1.
                    break;
            }

            if (index === targetIndex || targetIndex === -1) {
                return;
            }

            models.splice(targetIndex, 0, models.splice(index, 1)[0]); // TODO: this seems like an expensive way to swap two elements in an array!
            this.reindexLayers(models, lowestIndex);
        },

        /**
         * Group Widgets
         *
         * Generates a composite widget and moves the specified widgets from the scene widget collection to the composite widget.
         * The newly generated composite widget is then finally added to the scene.
         *
         * @param  {AbstractWidgetModel[]} widgets Array of <code>AbstractWidgetModel</code>s or <code>CompositeWidgetModel</code>s.
         * @param  {Object} options Options object (optional) where options.cause is specified on undo/redo to avoid re-registering the activity.
         * @param  {Function} cb Callback function (optional) that is triggered when the grouping process has been successfully completed.
         */
        groupWidgets(widgets, options, cb) {
            if (!this.canGroupWidgets(widgets)) {
                return;
            }

            const widgetsOfGroup = widgets.reduce((widgetsOfGroup, widget) => {
                if (widget instanceof CompositeWidgetModel) {
                    widget.resetGroupPositionOffset();
                    return widgetsOfGroup.concat(widget.get("group").models
                        .map((model) => model.clone()));
                }
                return [widget.clone()].concat(widgetsOfGroup);
            }, []);


            // Sort the widgets to make sure layerIndex is respected
            widgetsOfGroup.sort((a, b) => a.get("layerIndex") - b.get("layerIndex"));

            // Deletes the old widgets
            this.deleteWidgets(widgets, {cause: "group"});

            // Generate a new composite widget and with the newly grouped widgets
            const compositeWidget = new CompositeWidgetModel({"group": widgetsOfGroup});

            // Add the new composite widget to the current collection of widgets
            const highestWidgetIndex = this.getHighestIndex(widgetsOfGroup);
            this.addWidget(compositeWidget, {
                cause: "group",
                at: highestWidgetIndex - 1
            });

            const compositeWidgets = [compositeWidget];

            this.trigger("selectWidgets", compositeWidgets);

            if (options.cause === "undo" || options.cause === "redo") {
                options.activity.compositeWidgets = compositeWidgets;
                return;
            }
            this.reindexLayers(this.get("widgets").models);

            EditorActivityManager.add(new EditorActivities.WidgetsGroupedActivity(compositeWidgets, widgets, this));

            if (cb) {
                cb();
            }
        },

        /**
         * Ungroup Widgets
         *
         * Pulls out grouped widgets from a composite widget and adds them to the scene widget collection.
         * The specified composite widget(s) are then removed from the scene.
         *
         * @param  {CompositeWidgetModel[]} widgets Array of <code>CompositeWidgetModel</code>s.
         * @param  {Object} options Options object (optional) where options.cause is specified on undo/redo to avoid re-registering the activity.
         * @param  {Function} cb Callback function (optional) that is triggered when the ungrouping process has been successfully completed.
         */
        ungroupWidgets(widgets, options, cb) {
            if (!this.canUngroupWidgets(widgets)) {
                return;
            }

            const ungroupedWidgets = widgets.reduce((allWidgets, widget) => {
                if (!(widget instanceof CompositeWidgetModel)) {
                    return allWidgets;
                }

                widget.resetGroupPositionOffset();

                const widgets = widget.get("group").models;
                widgets.sort((a, b) => a.get("layerIndex") - b.get("layerIndex"));

                const ungroupedWidgets = widgets.map((model) => model.clone());
                ungroupedWidgets.forEach((widget, idx) => {
                    this.addWidget(widget, {
                        at: widget.get("layerIndex") + idx,
                        cause: "ungroup"
                    });
                });

                this.deleteWidget(widget, {cause: "ungroup"});

                return allWidgets.concat(ungroupedWidgets);
            }, []);

            this.trigger("selectWidgets", widgets.filter((w) => !(w instanceof CompositeWidgetModel)).concat(ungroupedWidgets));

            if (options.cause === "undo" || options.cause === "redo") {
                options.activity.groupWidgets = ungroupedWidgets;
                return;
            }

            EditorActivityManager.add(new EditorActivities.WidgetsUngroupedActivity(widgets, ungroupedWidgets, this));

            if (cb) {
                cb();
            }
        },

        /**
         * Validate if the given widgets can be grouped or not
         *
         * @param widgets (models)
         */
        canGroupWidgets(widgets) {
            if (!_.isArray(widgets) || widgets.length < 2) {
                return false;
            }

            const isDynamicProductWidget = widgets.some((widget) => widget.get("type") === "dynamicProductWidget");

            return !isDynamicProductWidget;
        },

        /**
         * Validate if the given widgets can be ungrouped or not
         *
         * @param composite widgets (models)
         */
        canUngroupWidgets(widgets) {
            if (!_.isArray(widgets) || widgets.length === 0) {
                return false;
            }

            const hasCompositeWidgets = widgets.some((widget) => widget instanceof CompositeWidgetModel);

            return hasCompositeWidgets;
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/SceneEditorMixin.js